import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { InvoiceBlockResponse } from '@/shared/api';
import { SortableBlockRow } from './SortableBlockRow';

interface Props {
  blocks: InvoiceBlockResponse[];
  previewNights: number;
  previewGuests: number;
  onDragEnd: (event: DragEndEvent) => void;
  onEdit: (block: InvoiceBlockResponse) => void;
  onDelete: (block: InvoiceBlockResponse) => void;
}

/**
 * Die sortierbare Liste der Posten.
 *
 * <p>Der `KeyboardSensor` ist kein Beiwerk: ohne ihn lässt sich die Reihenfolge nur mit der Maus
 * ändern, und der Griff war bis Sprint 55 zusätzlich hover-gebunden — auf Touch gab es diesen
 * Weg damit überhaupt nicht.</p>
 */
export function InvoiceBlocksList({
  blocks,
  previewNights,
  previewGuests,
  onDragEnd,
  onEdit,
  onDelete,
}: Props) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext
        items={blocks.map((block) => block.id)}
        strategy={verticalListSortingStrategy}
      >
        <ul className="border-line bg-card-alt/30 rounded-md border">
          {blocks.map((block) => (
            <SortableBlockRow
              key={block.id}
              block={block}
              previewNights={previewNights}
              previewGuests={previewGuests}
              onEdit={() => onEdit(block)}
              onDelete={() => onDelete(block)}
            />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}
